import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

interface UseAutoResizeTextareaOptions {
	/** Cap the grown height; past it the textarea scrolls internally. */
	maxHeight?: number;
	/** Floor the grown height so the field never collapses below a comfortable size. */
	minHeight?: number;
	/** Receives the raw (unclamped) scrollHeight after each measure — e.g. to derive a multi-line flag. */
	onResize?: (scrollHeight: number) => void;
}

/**
 * Grows a textarea to fit its content, clamped to an optional [min, max] range. The reset-to-auto
 * read is the only reliable way to shrink back on delete, so this owns the imperative height write
 * in one place rather than each composer hand-rolling it. Re-measures whenever `value` or the
 * field's available width changes and returns the measure fn for callers that also resize on direct
 * input. Width matters because text wrapping changes `scrollHeight`: retaining a measurement taken
 * while a drawer is settling can otherwise leave a short field several lines too tall.
 */
export const useAutoResizeTextarea = (
	ref: RefObject<HTMLTextAreaElement | null>,
	value: string,
	options: UseAutoResizeTextareaOptions = {},
) => {
	const optionsRef = useRef(options);
	const measuredWidthRef = useRef<number | undefined>(undefined);
	const observedElementRef = useRef<HTMLTextAreaElement | null>(null);
	const observerRef = useRef<ResizeObserver | undefined>(undefined);
	optionsRef.current = options;

	const resize = useCallback(
		(knownWidth?: number): number | undefined => {
			const el = ref.current;
			if (!el) return;

			// A textarea mounted into a collapsed pane has a real DOM node but no usable wrapping width.
			// Measuring it there makes every word wrap into a near-zero column and briefly writes the
			// maximum height before the pane's first ResizeObserver delivery corrects it. Leave the CSS
			// minimum in place until layout can give us a meaningful width.
			const width = knownWidth ?? measuredWidthRef.current ?? el.getBoundingClientRect().width;
			if (width <= 0) return;
			measuredWidthRef.current = width;

			const { maxHeight, minHeight, onResize } = optionsRef.current;
			el.style.height = 'auto';
			const { scrollHeight } = el;
			const capped = maxHeight !== undefined ? Math.min(scrollHeight, maxHeight) : scrollHeight;
			el.style.height = `${minHeight !== undefined ? Math.max(minHeight, capped) : capped}px`;
			if (maxHeight !== undefined) {
				el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
			}
			onResize?.(scrollHeight);
			return scrollHeight;
		},
		[ref],
	);

	// No deps on purpose: a caller that conditionally renders its field swaps the element behind `ref`
	// without changing anything this hook can depend on. An observer left on the detached node reports
	// its zero width, and that zero then blocks every measure of the replacement. Declared before the
	// value effect so a swap and a value change in one commit measure the new element's width.
	useLayoutEffect(() => {
		const el = ref.current;
		if (el === observedElementRef.current) return;
		observedElementRef.current = el;
		measuredWidthRef.current = undefined;
		observerRef.current?.disconnect();
		observerRef.current = undefined;
		if (!el || typeof ResizeObserver === 'undefined') return;

		const observer = new ResizeObserver(([entry]) => {
			const width = entry.contentRect.width;
			// Remember a collapsed width too. Otherwise a value change while hidden could reuse the last
			// positive width as permission to read a zero-width element's misleading scrollHeight.
			if (width <= 0) {
				measuredWidthRef.current = width;
				return;
			}
			// Changing height triggers the observer too. Ignore it unless wrapping width changed, while
			// still measuring the first delivered layout after the field mounted. Even a fractional width
			// change can move a word across the wrapping boundary, so compare the reported width exactly.
			if (width === measuredWidthRef.current) return;
			measuredWidthRef.current = width;
			resize(width);
		});
		observer.observe(el);
		observerRef.current = observer;
	});

	// Height is visual geometry, so establish it before paint. A normal effect briefly exposes the
	// browser's default textarea height on mount and refresh before correcting it.
	useLayoutEffect(() => {
		resize();
	}, [value, resize]);

	useEffect(
		() => () => {
			observerRef.current?.disconnect();
			observerRef.current = undefined;
			// Lets StrictMode's simulated remount observe the same element again.
			observedElementRef.current = null;
		},
		[],
	);

	return resize;
};
